<template>
    <section class="admin-details">
  
      <div class="details-toolbar">
  
        <div>
          <h3>
            Current Admins
          </h3>
  
          <p>
            Users authorized to access the admin portal.
          </p>
        </div>
  
        <button
          type="button"
          class="refresh-button"
          :disabled="loading"
          @click="loadAdmins"
        >
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
  
      </div>
  
      <div
        v-if="loading"
        class="state-card"
      >
        Loading admins...
      </div>
  
      <div
        v-else-if="errorMessage"
        class="state-card error"
      >
        {{ errorMessage }}
      </div>
  
      <div
        v-else-if="admins.length === 0"
        class="state-card"
      >
        No admin records were found.
      </div>
  
      <div
        v-else
        class="admin-table-wrapper"
      >
        <table class="admin-table">
  
          <thead>
            <tr>
              <th>
                Name
              </th>
  
              <th>
                Email
              </th>
  
              <th>
                Access
              </th>

              <th class="action-heading">
                <span class="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
  
          <tbody>
            <tr
              v-for="admin in admins"
              :key="admin.id"
            >
              <td>
                <div class="admin-name">
                  {{ admin.name }}
                </div>
  
                <!-- <div class="admin-id">
                  {{ admin.adminId }}
                </div> -->
              </td>
  
              <td>
                {{ admin.email }}
              </td>
  
              <td>
                <div class="role-list">
  
                  <span
                    v-for="role in admin.type"
                    :key="role"
                    class="role-pill"
                  >
                    {{ role }}
                  </span>
  
                  <span
                    v-if="admin.type.length === 0"
                    class="empty-role"
                  >
                    No roles
                  </span>
  
                </div>
              </td>

              <td class="action-cell">
                <button
                  v-if="canManageAdmins"
                  type="button"
                  class="edit-button"
                  :aria-label="`Edit ${admin.name}`"
                  :title="`Edit ${admin.name}`"
                  @click="$emit('edit', admin)"
                >
                  <span class="material-icons-outlined" aria-hidden="true">
                    edit
                  </span>
                </button>
              </td>
            </tr>
          </tbody>
  
        </table>
      </div>
  
    </section>
  </template>
  
  <script>
  import {
    onMounted,
    ref,
    watch
  } from "vue";
  
  import {
    fetchAdmins
  } from "@/firebase/firestore";
  
  export default {
    name:
      "AdminDetails",

    emits: [
      "edit"
    ],

    props: {
      refreshKey: {
        type: Number,
        default: 0
      },
      canManageAdmins: {
        type: Boolean,
        default: false
      }
    },
  
    setup(props) {
  
      const admins =
        ref([]);
  
      const loading =
        ref(false);
  
      const errorMessage =
        ref("");
  
      const loadAdmins =
        async () => {
  
          loading.value =
            true;
  
          errorMessage.value =
            "";
  
          try {
  
            admins.value =
              await fetchAdmins();
  
          } catch (error) {
  
            console.error(
              "❌ Unable to load admins:",
              error
            );
  
            errorMessage.value =
              "Unable to load admin records.";
  
          } finally {
  
            loading.value =
              false;
          }
        };
  
      onMounted(
        loadAdmins
      );

      watch(
            () => props.refreshKey,
            loadAdmins
        );
  
      return {
        admins,
        loading,
        errorMessage,
        loadAdmins
      };
    }
  };
  </script>
  
  <style scoped>
  .admin-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .details-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
  }
  
  .details-toolbar h3 {
    margin: 0;
    font-size: 1.15rem;
  }
  
  .details-toolbar p {
    margin: 5px 0 0;
    color: #747e79;
  }
  
  .refresh-button {
    padding: 9px 15px;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #25322c;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
  }
  
  .refresh-button:hover:not(:disabled) {
    background: #f4f8f5;
  }
  
  .refresh-button:disabled {
    cursor: wait;
    opacity: 0.65;
  }
  
  .state-card {
    padding: 24px;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
    color: #737d78;
  }
  
  .state-card.error {
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
  }
  
  .admin-table-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e7e4;
    border-radius: 12px;
    background: #ffffff;
  }
  
  .admin-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .admin-table th,
  .admin-table td {
    padding: 16px 18px;
    border-bottom: 1px solid #edf0ee;
    text-align: left;
    vertical-align: top;
  }
  
  .admin-table th {
    background: #f7f9f8;
    color: #59645f;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  
  .admin-table tbody tr:last-child td {
    border-bottom: 0;
  }
  
  .admin-table tbody tr:hover {
    background: #fbfdfb;
  }
  
  .admin-name {
    color: #1c2923;
    font-weight: 600;
  }
  
  .admin-id {
    margin-top: 4px;
    color: #929b96;
    font-size: 0.76rem;
    word-break: break-all;
  }
  
  .role-list {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }
  
  .role-pill {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
    background: #edf7f1;
    color: #178554;
    font-size: 0.8rem;
    font-weight: 600;
  }
  
  .empty-role {
    color: #959e99;
    font-size: 0.85rem;
  }

  .action-heading,
  .action-cell {
    width: 64px;
    text-align: right !important;
  }

  .edit-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #d7ded9;
    border-radius: 9px;
    background: #ffffff;
    color: #53605a;
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
  }

  .edit-button:hover {
    border-color: #9bc9b2;
    background: #edf7f1;
    color: #178554;
  }

  .edit-button:focus-visible {
    outline: 3px solid rgba(24, 171, 105, 0.18);
    outline-offset: 2px;
  }

  .edit-button .material-icons-outlined {
    font-size: 19px;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  @media (max-width: 700px) {
    .details-toolbar {
      align-items: flex-start;
      flex-direction: column;
    }
  
    .refresh-button {
      width: 100%;
    }
  }
  </style>
